import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { EmailVerificationStatusResponse } from '@/core/generated/api';
import { isBadRequestError } from '@/core/api/badRequestError';
import { emailVerificationService } from '@/entities/EmailVerification/api/emailVerificationService';
import { emailBindingFormToRequest } from '@/entities/EmailVerification/mappers';
import type { EmailBindingFormValues } from '@/entities/EmailVerification/model/schema';
import type { EmailBindingTarget } from '@/entities/EmailVerification/model/types';
import { useCountdown } from '@/shared/hooks/useCountdown';
import { getErrorMessage } from '@/shared/lib/errors';
import { RequestStatus } from '@/shared/model/requestStatus';

/** Управляет привязкой почты и повторным запросом письма */
export const useProfileEmailBinding = (
    form: UseFormReturn<EmailBindingFormValues>,
    target: EmailBindingTarget,
    initialEmail: string,
) => {
    const [status, setStatus] = useState<EmailVerificationStatusResponse | null>(null);
    const [requestStatus, setRequestStatus] = useState(RequestStatus.Loading);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [deadline, setDeadline] = useState<number | null>(null);
    const [storedEmail, setStoredEmail] = useState(initialEmail.trim());
    const [sentEmail, setSentEmail] = useState<string | null>(null);
    const mounted = useRef(false);
    const revision = useRef(0);
    const sending = useRef(false);
    const secondsBeforeResend = useCountdown(deadline);
    const currentEmail = form.watch('email').trim();
    const isStoredEmail = currentEmail.toLowerCase() === storedEmail.toLowerCase();
    const isVerifiedEmail = status?.isEmailVerified === true && isStoredEmail;

    /** Обновляет состояние подтверждения выбранной почты */
    const refreshStatus = useCallback(async () => {
        const requestRevision = ++revision.current;
        setRequestStatus(RequestStatus.Loading);
        setStatusError(null);

        try {
            const response = await emailVerificationService.getStatus(target.appUserId);

            if (!mounted.current || requestRevision !== revision.current) return;

            setStatus(response);
            setDeadline(Date.now() + (response.secondsBeforeResend ?? 0) * 1000);
            setRequestStatus(RequestStatus.Success);
        } catch (error: unknown) {
            if (!mounted.current || requestRevision !== revision.current) return;

            setStatusError(isBadRequestError(error)
                ? 'Не удалось получить состояние почты'
                : getErrorMessage(error, 'Не удалось получить состояние почты'));

            setRequestStatus(RequestStatus.Error);
        }
    }, [target.appUserId]);

    useEffect(() => {
        mounted.current = true;
        void refreshStatus();

        return () => {
            mounted.current = false;
        };
    }, [refreshStatus]);

    const canSend = requestStatus === RequestStatus.Success
        && secondsBeforeResend === 0 && !isVerifiedEmail;

    /** Запрашивает письмо подтверждения для введённого адреса */
    const onSubmit = async (values: EmailBindingFormValues) => {
        if (!canSend || sending.current) return;

        sending.current = true;
        form.clearErrors('root.server');

        try {
            await emailVerificationService.bindEmail(emailBindingFormToRequest(target, values));

            if (!mounted.current) return;

            setStoredEmail(values.email);
            setSentEmail(values.email);
            await refreshStatus();
        } catch (error: unknown) {
            if (!mounted.current) return;

            if (isBadRequestError(error)) {
                await refreshStatus();

                return;
            }

            form.setError('root.server', {
                type: 'server',
                message: getErrorMessage(error, 'Не удалось отправить письмо'),
            });
        } finally {
            sending.current = false;
        }
    };

    return {
        onSubmit,
        refreshStatus,
        canSend,
        isVerifiedEmail,
        isStoredEmail,
        hasEmail: status?.hasEmail === true,
        requestStatus,
        statusError,
        secondsBeforeResend,
        sentEmail,
    };
};
